import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { UserDenormalizedWithoutLook } from "./user"
import { PowerDenormalized } from "./power"
import { LivePowerId, LivePowerIdSchema } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { LiveDenormalized } from "./live"
import { SpaceDenormalized } from "./space"

/**
 * コイン消費内訳のスコープ別消費量。
 */
export const CoinSpentScope = z.object({
  scope: z.number().describe("コインスコープ。0: Free, 1: IAP, 2: IAB, 3: Web, 4: Compensation"),
  amount: z.number().describe("そのスコープでの消費量。"),
})

export type CoinSpentScope = z.infer<typeof CoinSpentScope>

/**
 * コイン消費内訳。有償・無償の内訳を記録する。
 */
export const CoinSpent = z.object({
  total: z.number().describe("コイン消費の合計値。"),
  scopes: z.array(CoinSpentScope).describe("スコープ別の消費内訳。"),
})

export type CoinSpent = z.infer<typeof CoinSpent>

export const LivePowerHistoryDocumentBody = z.intersection(
  z.object({
    power: PowerDenormalized,
    user: UserDenormalizedWithoutLook,
    space: SpaceDenormalized.optional().describe("パワーが送信されたスペースの非正規化情報。リアルタイム書き込み導入後に追加されたフィールド。"),
    live: LiveDenormalized.optional().describe("パワーが送信されたライブの非正規化情報。リアルタイム書き込み導入後に追加されたフィールド。"),
    nonce: z.string().optional().describe("コイントランザクションのnonce。coin-transactions との紐付けに使用する。リアルタイム書き込み導入後に追加されたフィールド。"),
    platform: z.string().optional().describe("パワー送信時のプラットフォーム (ios/android)。リアルタイム書き込み導入後に追加されたフィールド。"),
    coin_spent: CoinSpent.optional().describe("コイン消費の内訳（有償・無償）。リアルタイム書き込み導入後に追加されたフィールド。"),
  }),
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  }),
)

export type LivePowerHistoryDocumentBody = z.infer<typeof LivePowerHistoryDocumentBody>

export const LivePowerHistoryDocument = z.intersection(
  LivePowerHistoryDocumentBody,
  z.object({
    id: LivePowerIdSchema,
  })
)

export type LivePowerHistoryDocument = z.infer<typeof LivePowerHistoryDocument>

export class LivePowerHistoryDocuments {

  static toDocumentData(doc: LivePowerHistoryDocument): object {
    return {
      ...doc,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): LivePowerHistoryDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: LivePowerId(doc.id),
      power: data["power"],
      user: data["user"],
      space: data["space"],
      live: data["live"],
      nonce: data["nonce"],
      platform: data["platform"],
      coin_spent: data["coin_spent"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
